/**
 * FormField — label + input wrapper + error/help text.
 *
 * Usage:
 *   <FormField label="Email" error={errors.email?.message} required>
 *     <Input type="email" {...register('email')} />
 *   </FormField>
 *
 * The label automatically associates with the first input child via htmlFor.
 * If you need a different control, pass `htmlFor` explicitly.
 *
 * The `tone` prop controls label/help/error contrast for the auth-page
 * glass background (white labels on a colored background).
 */

import { Children, cloneElement, isValidElement, useId } from 'react'

const TONES = {
  default: {
    label: 'text-ink-secondary',
    help:  'text-ink-muted',
    error: 'text-danger',
  },
  // Auth-page tone — white text with subtle shadow for legibility on the
  // pastel gradient. The shadow lifts the text off the busy background
  // without making it harsh.
  glass: {
    label: 'text-white/90 [text-shadow:0_1px_2px_rgba(15,40,75,0.15)]',
    help:  'text-white/75 [text-shadow:0_1px_2px_rgba(15,40,75,0.12)]',
    error: 'text-rose-100 [text-shadow:0_1px_2px_rgba(150,30,60,0.25)]',
  },
}

export default function FormField({
  label,
  htmlFor,
  required = false,
  error,
  help,
  tone = 'default',
  className = '',
  children,
}) {
  const generatedId = useId()
  const fieldId = htmlFor || generatedId

  // If the immediate child is a known control, inject an id so the <label>
  // points at the right input even when the caller didn't specify one.
  const wired = Children.map(children, (child) => {
    if (!isValidElement(child)) return child
    if (child.props.id) return child
    return cloneElement(child, { id: fieldId, invalid: !!error || child.props.invalid })
  })

  const t = TONES[tone] || TONES.default

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && (
        <label htmlFor={fieldId} className={`text-xs font-medium ${t.label}`}>
          {label}
          {required && <span className="text-danger ms-0.5" aria-hidden="true">*</span>}
        </label>
      )}
      {wired}
      {error ? (
        <p role="alert" className={`text-xs ${t.error}`}>{error}</p>
      ) : help ? (
        <p className={`text-xs ${t.help}`}>{help}</p>
      ) : null}
    </div>
  )
}
